// StopRow — Phase 4 Plan 04-03 (D-75, DIS-07).
//
// Single row in NewTrip multi-stop form:
//   - drag handle (24×24 SVG inside 48×48 hit zone) with touch-action: none
//     CSS rule on HANDLE ONLY (per 04-RESEARCH §Pitfall 5 — never on whole row).
//   - role chip (pickup / dropoff / intermediate) — passive visual marker.
//   - StopInput (HERE Places autocomplete) embedded.
//   - cargo + planned_arrival_window_{start,end} (native datetime-local) inputs.
//   - delete X button (44×44 minimum).
//   - aria-roledescription="sortable" on container; aria-label on handle.

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";

import type { StopRole } from "../api/trips";
import { TOUCH_TARGET_MIN } from "../lib/tokens";
import { LabeledField, datetimeInputStyle, fieldInputStyle } from "./LabeledField";
import { type StopAddress, StopInput } from "./StopInput";

export interface StopRowData {
  id: string; // dnd-kit unique identifier (we use index-based key)
  address: StopAddress;
  cargo_description: string;
  planned_arrival_window_start: string; // ISO datetime-local format
  planned_arrival_window_end: string;
}

export interface StopRowProps {
  row: StopRowData;
  index: number;
  total: number;
  role: StopRole;
  onChange: (next: StopRowData) => void;
  onDelete: () => void;
  canDelete: boolean;
  locale?: string;
}

const ROLE_CHIP_COLORS: Record<StopRole, { bg: string; text: string }> = {
  pickup: { bg: "#DBEAFE", text: "#1E40AF" },
  dropoff: { bg: "#FEE2E2", text: "#991B1B" },
  intermediate: { bg: "#F1F5F9", text: "#475569" },
};

/** Labelled native datetime-local field. NOT telegram-ui <Input> — that wrapper
 *  forces appearance:none on the inner element and blanks the iOS picker. Uses
 *  the shared LabeledField look so it matches address + cargo. */
function DateTimeField({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  testId: string;
}): React.JSX.Element {
  return (
    <LabeledField label={label}>
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        style={datetimeInputStyle}
      />
    </LabeledField>
  );
}

export function StopRow({
  row,
  index,
  total,
  role,
  onChange,
  onDelete,
  canDelete,
  locale,
}: StopRowProps): React.JSX.Element {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });
  const palette = ROLE_CHIP_COLORS[role];

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: "white",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    boxShadow: isDragging ? "0 6px 16px rgba(0,0,0,0.15)" : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`stop-row-${index}`}
      aria-roledescription="sortable"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {/* Drag handle — touch-action: none ONLY here (Pitfall 5). */}
        <button
          type="button"
          aria-label={
            t("dispatcher:newtrip.drag_label", {
              defaultValue: "Drag to reorder stop {{position}}",
              position: index + 1,
            }) as string
          }
          {...attributes}
          {...listeners}
          data-testid={`stop-drag-handle-${index}`}
          className="drag-handle"
          style={{
            touchAction: "none",
            cursor: "grab",
            background: "transparent",
            border: "none",
            padding: 12,
            minWidth: TOUCH_TARGET_MIN,
            minHeight: TOUCH_TARGET_MIN,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="9" cy="6" r="1.5" fill="currentColor" />
            <circle cx="9" cy="12" r="1.5" fill="currentColor" />
            <circle cx="9" cy="18" r="1.5" fill="currentColor" />
            <circle cx="15" cy="6" r="1.5" fill="currentColor" />
            <circle cx="15" cy="12" r="1.5" fill="currentColor" />
            <circle cx="15" cy="18" r="1.5" fill="currentColor" />
          </svg>
        </button>
        {/* Role chip */}
        <span
          data-testid={`stop-role-chip-${index}`}
          style={{
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: 12,
            backgroundColor: palette.bg,
            color: palette.text,
            fontSize: 12,
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        >
          {role}
        </span>
        <small style={{ color: "#94A3B8", fontSize: 12 }}>
          {index + 1} / {total}
        </small>
        <div style={{ flex: 1 }} />
        {canDelete ? (
          <button
            type="button"
            onClick={onDelete}
            data-testid={`stop-delete-${index}`}
            aria-label={t("dispatcher:newtrip.stop_delete", "Delete stop") as string}
            style={{
              minWidth: TOUCH_TARGET_MIN,
              minHeight: TOUCH_TARGET_MIN,
              background: "transparent",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              color: "#DC2626",
              fontSize: 18,
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {/* StopInput (HERE Places) */}
      <StopInput
        value={row.address}
        onChange={(addr) => onChange({ ...row, address: addr })}
        locale={locale}
        index={index}
      />

      {/* Cargo + windows — one consistent LabeledField look (Phase 12 UAT
          2026-06-16): every stop field is a labelled native input on theme
          colours, so address / cargo / datetime all match. */}
      <div style={{ marginTop: 6 }}>
        <LabeledField label={t("dispatcher:newtrip.cargo_label", "Cargo") as string}>
          <input
            type="text"
            value={row.cargo_description}
            onChange={(e) => onChange({ ...row, cargo_description: e.target.value })}
            placeholder={t("dispatcher:newtrip.cargo_placeholder", "Cargo description") as string}
            data-testid={`stop-cargo-${index}`}
            style={fieldInputStyle}
          />
        </LabeledField>
        <DateTimeField
          label={t("dispatcher:newtrip.window_start_label", "Arrival window — from") as string}
          value={row.planned_arrival_window_start}
          onChange={(v) => onChange({ ...row, planned_arrival_window_start: v })}
          testId={`stop-window-start-${index}`}
        />
        <DateTimeField
          label={t("dispatcher:newtrip.window_end_label", "Arrival window — to") as string}
          value={row.planned_arrival_window_end}
          onChange={(v) => onChange({ ...row, planned_arrival_window_end: v })}
          testId={`stop-window-end-${index}`}
        />
      </div>
    </div>
  );
}
